import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { WEAPONS, type WeaponType } from '../game/weapons';

// Each model owns its geometry/materials; disposing one never affects another.
export function buildWeapon(type: WeaponType): THREE.Group {
  const root = new THREE.Group();
  root.name = `weapon-${type}`;
  const material = (color: THREE.ColorRepresentation, roughness = 0.7, metalness = 0) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness });
  const add = (name: string, geometry: THREE.BufferGeometry, surface: THREE.Material, y: number) => {
    const mesh = new THREE.Mesh(geometry, surface);
    mesh.name = name;
    mesh.position.y = y;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
    return mesh;
  };

  if (type === 'pipe') {
    const steel = material(WEAPONS.pipe.color, 0.29, 0.82);
    add('hollow-steel-tube', new THREE.CylinderGeometry(0.1, 0.1, 1.45, 10, 1, true), steel, 0.605);
    const inside = material('#30404b', 0.85, 0.3);
    inside.side = THREE.BackSide;
    add('dark-tube-interior', new THREE.CylinderGeometry(0.071, 0.071, 1.45, 10, 1, true), inside, 0.605);
    for (const y of [-0.12, 1.33]) {
      const rim = add('open-metal-rim', new THREE.RingGeometry(0.071, 0.1, 10), steel, y);
      rim.rotation.x = y < 0 ? Math.PI / 2 : -Math.PI / 2;
    }
    const coupling = [
      [0.1, -0.065], [0.124, -0.048], [0.124, 0.048], [0.1, 0.065],
    ].map(([x, y]) => new THREE.Vector2(x, y));
    add('rusted-coupling', new THREE.LatheGeometry(coupling, 10), material('#ad6b42', 0.82, 0.4), 1.05);
    const grip = [
      [0, -0.115], [0.108, -0.115], [0.108, -0.085],
      [0.101, -0.07], [0.101, 0.075], [0.108, 0.09], [0.108, 0.115], [0, 0.115],
    ].map(([x, y]) => new THREE.Vector2(x, y));
    add('rubber-grip', new THREE.LatheGeometry(grip, 8), material('#252c31'), 0.015);
  } else if (type === 'plank') {
    const outline = new THREE.Shape();
    outline.moveTo(-0.135, -0.875);
    outline.lineTo(0.135, -0.875);
    outline.lineTo(0.135, 0.875);
    outline.lineTo(0.055, 0.76);
    outline.lineTo(0.018, 0.86);
    outline.lineTo(-0.04, 0.72);
    outline.lineTo(-0.08, 0.875);
    outline.lineTo(-0.135, 0.81);
    outline.closePath();
    const timber = new THREE.ExtrudeGeometry(outline, { depth: 0.15, bevelEnabled: false, steps: 1 });
    timber.translate(0, 0, -0.075);
    add('splintered-timber-beam', timber, material(WEAPONS.plank.color), 0.755);
    const grain: number[] = [];
    for (const z of [-0.0755, 0.0755]) {
      for (let index = 0; index < 4; index++) {
        const x = -0.105 + index * 0.065;
        const y = 0.05 + index * 0.08;
        const end = 1.5 - index * 0.1;
        const bend = index % 2 ? 0.015 : -0.015;
        grain.push(x, y, z, x + bend, end, z, x + bend + 0.009, end, z,
          x, y, z, x + bend + 0.009, end, z, x + 0.009, y, z);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(grain, 3));
    geometry.computeVertexNormals();
    const grooves = material('#986335');
    grooves.side = THREE.DoubleSide;
    add('wood-grain', geometry, grooves, 0);
    const iron = material('#53616a', 0.5, 0.65);
    for (const y of [0.3, 1.25]) {
      const brace = new THREE.BoxGeometry(0.281, 0.065, 0.161);
      const positions = brace.getAttribute('position');
      for (let index = 0; index < positions.count; index++) {
        const inset = positions.getY(index) > 0 ? 0.96 : 1;
        positions.setX(index, positions.getX(index) * inset);
        positions.setZ(index, positions.getZ(index) * inset);
      }
      brace.computeVertexNormals();
      add('iron-brace', brace, iron, y);
    }
  } else if (type === 'chair') {
    const steel = material('#acb8c4', 0.4, 0.75);
    const plate = material('#728497', 0.55, 0.6);
    const rubber = material('#252c33');
    const rod = (name: string, from: number[], to: number[], radius = 0.035) => {
      const a = new THREE.Vector3(...from), b = new THREE.Vector3(...to);
      const mesh = add(name, new THREE.CylinderGeometry(radius, radius, a.distanceTo(b), 8), steel, 0);
      mesh.position.copy(a).add(b).multiplyScalar(0.5);
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.sub(a).normalize());
    };
    for (const x of [-0.34, 0.34]) {
      rod('back-frame', [x, -0.12, -0.12], [x, 1.22, 0]);
      rod('front-frame', [x, -0.12, 0.54], [x, 0.65, 0.02]);
      rod('folding-brace', [x, 0.18, 0.41], [x, 0.56, -0.04], 0.022);
      for (const z of [-0.12, 0.54]) {
        const foot = add(z > 0 ? 'front-foot' : 'back-foot', new THREE.CylinderGeometry(0.045, 0.045, 0.07, 8), rubber, -0.14);
        foot.position.set(x, foot.position.y, z);
      }
      const hinge = add('folding-rivet', new THREE.CylinderGeometry(0.055, 0.055, 0.025, 8), steel, 0.61);
      hinge.position.x = x;
      hinge.rotation.z = Math.PI / 2;
    }
    rod('back-crossbar', [-0.34, 0.18, -0.09], [0.34, 0.18, -0.09], 0.025);
    rod('front-crossbar', [-0.34, 0.18, 0.36], [0.34, 0.18, 0.36], 0.025);
    rod('top-frame', [-0.34, 1.22, 0], [0.34, 1.22, 0]);
    add('backrest-metal-panel', new THREE.BoxGeometry(0.61, 0.31, 0.055, 4, 3), plate, 1.04);
    add('folding-seat', new THREE.BoxGeometry(0.65, 0.055, 0.53, 4, 1, 4), plate, 0.62).position.z = 0.255;
    for (const child of root.children) {
      const mesh = child as THREE.Mesh;
      const part = mesh.name === 'folding-seat' ? 1 : /^(front-|folding-brace)/.test(mesh.name) ? 2 : 0;
      mesh.geometry.setAttribute('chairPart', new THREE.Float32BufferAttribute(new Float32Array(mesh.geometry.getAttribute('position').count).fill(part), 1));
    }
  } else if (type === 'warhammer') {
    const steel = material('#596471', 0.45, 0.8);
    const face = material('#aab9c8', 0.3, 0.85);
    const leather = material('#463226', 0.95);
    const bronze = material('#bf9655', 0.5, 0.65);
    add('long-warhammer-shaft', new THREE.CylinderGeometry(0.065, 0.08, 1.65, 8), leather, 0.59);
    add('reinforced-shaft', new THREE.CylinderGeometry(0.085, 0.085, 0.37, 8), steel, 1.18);
    const head = add('massive-square-hammerhead', new THREE.BoxGeometry(0.68, 0.58, 0.92), steel, 1.52);
    // Chamfered corners keep the head massive without a sharp cutting edge.
    const p = head.geometry.getAttribute('position');
    for (let i = 0; i < p.count; i++) {
      if (p.getY(i) > 0) { p.setX(i, p.getX(i) * 0.86); p.setZ(i, p.getZ(i) * 0.94); }
    }
    head.geometry.computeVertexNormals();
    for (const z of [-0.48, 0.48]) {
      add('blunt-striking-face', new THREE.BoxGeometry(0.64, 0.5, 0.09), face, 1.5).position.z = z;
    }
    add('bronze-head-band', new THREE.BoxGeometry(0.7, 0.6, 0.12), bronze, 1.52);
    for (const y of [-0.23, -0.07, 0.1, 0.27, 0.44]) {
      add('grip-wrap', new THREE.TorusGeometry(0.078, 0.015, 4, 8), bronze, y).rotation.x = Math.PI / 2;
    }
    add('heavy-pommel', new THREE.SphereGeometry(0.12, 8, 5), steel, -0.3);
  } else if (type === 'greatsword') {
    const edge = material('#bfcbd5', 0.28, 0.85);
    const iron = material('#414a55', 0.5, 0.8);
    const leather = material('#372a25', 0.95);
    const blade = new THREE.Shape();
    blade.moveTo(-0.25, 0.24);
    blade.lineTo(-0.34, 1.58);
    blade.lineTo(0, 1.92);
    blade.lineTo(0.34, 1.58);
    blade.lineTo(0.25, 0.24);
    blade.closePath();
    const geometry = new THREE.ExtrudeGeometry(blade, { depth: 0.09, bevelEnabled: true, bevelSize: 0.025, bevelThickness: 0.015, bevelSegments: 1, steps: 1 });
    geometry.translate(0, 0, -0.045);
    add('massive-beveled-blade', geometry, edge, 0);
    const face = new THREE.Shape();
    face.moveTo(-0.19, 0.29); face.lineTo(-0.265, 1.55); face.lineTo(0, 1.81);
    face.lineTo(0.265, 1.55); face.lineTo(0.19, 0.29); face.closePath();
    const slab = new THREE.ExtrudeGeometry(face, { depth: 0.126, bevelEnabled: false, steps: 1 });
    slab.translate(0, 0, -0.063);
    add('blackened-iron-blade-face', slab, iron, 0);
    add('heavy-crossguard', new THREE.BoxGeometry(0.78, 0.1, 0.19), iron, 0.2);
    add('long-two-hand-hilt', new THREE.CylinderGeometry(0.065, 0.065, 0.43, 8), leather, -0.065);
    for (let i = 0; i < 5; i++) {
      add('grip-wrap', new THREE.TorusGeometry(0.067, 0.012, 4, 8), iron, -0.23 + i * 0.075).rotation.x = Math.PI / 2;
    }
    add('steel-pommel', new THREE.CylinderGeometry(0.09, 0.08, 0.1, 8), iron, -0.32);
    const scars = material('#20252c');
    for (const z of [-0.064, 0.064]) for (let i = 0; i < 3; i++) {
      const mark = add('battle-scar', new THREE.BoxGeometry(0.18, 0.012, 0.003), scars, 0.65 + i * 0.32);
      mark.position.set(-0.08 + i * 0.05, mark.position.y, z);
      mark.rotation.z = -0.35;
    }
  } else if (type === 'bazooka') {
    const olive = material('#54623d', 0.6, 0.35);
    const steel = material('#303942', 0.4, 0.75);
    const dark = material('#0c141b');
    add('launcher-tube', new THREE.CylinderGeometry(0.15, 0.17, 1.1, 12, 1, true), olive, 0.26);
    for (const y of [-0.3, 0.82]) {
      const rim = add('open-launcher-rim', new THREE.RingGeometry(0.12, 0.18, 12), steel, y);
      rim.rotation.x = y > 0 ? -Math.PI / 2 : Math.PI / 2;
      const bore = add('recessed-launcher-bore', new THREE.CircleGeometry(0.12, 12), dark, y > 0 ? y - 0.06 : y + 0.06);
      bore.rotation.x = rim.rotation.x;
    }
    add('pistol-grip', new THREE.BoxGeometry(0.1, 0.12, 0.23), steel, 0.02).position.z = 0.2;
    add('support-grip', new THREE.BoxGeometry(0.1, 0.14, 0.17), steel, 0.22).position.z = 0.13;
    add('shoulder-pad', new THREE.BoxGeometry(0.2, 0.3, 0.1), dark, -0.1).position.z = 0.17;
    const sight = add('raised-sight', new THREE.TorusGeometry(0.065, 0.018, 4, 10), steel, 0.45);
    sight.rotation.x = Math.PI / 2;
    sight.position.z = -0.22;
    add('warning-band', new THREE.CylinderGeometry(0.153, 0.153, 0.09, 12, 1, true), material('#f6c64c'), 0.66);
  } else if (type === 'shotgun') {
    const steel = material('#49505a', 0.32, 0.8);
    const wood = material('#754125', 0.76);
    const dark = material('#111820');
    const grip = new THREE.Shape();
    grip.moveTo(-0.04, 0.07);
    grip.lineTo(0.08, 0.05);
    grip.lineTo(0.17, -0.22);
    grip.lineTo(0.02, -0.25);
    grip.lineTo(-0.055, -0.08);
    grip.closePath();
    const gripGeometry = new THREE.ExtrudeGeometry(grip, { depth: 0.115, bevelEnabled: true, bevelSize: 0.012, bevelThickness: 0.012, bevelSegments: 1, steps: 1 });
    // The model's +Y is the firing axis; +Z points down when carried.
    gripGeometry.rotateY(-Math.PI / 2);
    gripGeometry.translate(0.0575, 0, 0);
    add('cut-walnut-pistol-grip', gripGeometry, wood, 0);
    add('break-action-receiver', new THREE.BoxGeometry(0.18, 0.22, 0.14), steel, 0.12);
    for (const x of [-0.061, 0.061]) {
      add('short-open-barrel', new THREE.CylinderGeometry(0.057, 0.057, 0.46, 12, 1, true), steel, 0.45).position.x = x;
      const rim = add('cut-muzzle-rim', new THREE.RingGeometry(0.041, 0.057, 12), steel, 0.681);
      rim.position.x = x;
      rim.rotation.x = -Math.PI / 2;
      const bore = add('recessed-barrel-bore', new THREE.CircleGeometry(0.041, 12), dark, 0.65);
      bore.position.x = x;
      bore.rotation.x = -Math.PI / 2;
    }
    add('fixed-walnut-fore-end', new THREE.BoxGeometry(0.15, 0.23, 0.085), wood, 0.32).position.z = 0.055;
    const guard = add('trigger-guard', new THREE.TorusGeometry(0.055, 0.011, 5, 12), steel, 0.065);
    guard.rotation.y = Math.PI / 2;
    guard.position.z = 0.105;
    add('trigger', new THREE.BoxGeometry(0.015, 0.04, 0.035), dark, 0.08).position.z = 0.09;
    add('barrel-rib', new THREE.BoxGeometry(0.023, 0.44, 0.018), steel, 0.45).position.z = -0.051;
    add('front-bead', new THREE.SphereGeometry(0.012, 6, 4), steel, 0.65).position.z = -0.069;
  } else {
    const glass = material('#20854c', 0.18, 0.1);
    glass.transparent = true;
    glass.opacity = 0.85;
    const profile = [
      [0, -0.12], [0.048, -0.12], [0.048, 0.11], [0.063, 0.16],
      [0.112, 0.205], [0.135, 0.26], [0.135, 0.55], [0.12, 0.63], [0, 0.63],
    ].map(([x, y]) => new THREE.Vector2(x, y));
    add('green-glass-bottle', new THREE.LatheGeometry(profile, 12), glass, 0);
    add('bottle-lip', new THREE.CylinderGeometry(0.061, 0.061, 0.035, 12), material('#11673d', 0.2), -0.1025);
    add('cream-paper-label', new THREE.CylinderGeometry(0.137, 0.137, 0.19, 12, 1, true), material('#fff0b5'), 0.39);
    add('red-label-stripe', new THREE.CylinderGeometry(0.138, 0.138, 0.055, 12, 1, true), material('#d64428'), 0.39);
  }
  if (type === 'pipe') {
    root.traverse(object => {
      const mesh = object as THREE.Mesh;
      if (!mesh.geometry) return;
      mesh.updateMatrix();
      mesh.geometry.applyMatrix4(mesh.matrix);
      mesh.position.set(0, 0, 0);
      mesh.rotation.set(0, 0, 0);
      const position = mesh.geometry.getAttribute('position');
      for (let i = 0; i < position.count; i++) {
        position.setX(i, position.getX(i) + Math.max(0, position.getY(i) - 0.78) * 0.46);
      }
      position.needsUpdate = true;
      mesh.geometry.computeVertexNormals();
    });
  }
  if (type === 'greatsword') {
    // Present the slab's broad face to the broadcast camera instead of its thin edge.
    for (const child of root.children) {
      const mesh = child as THREE.Mesh;
      mesh.updateMatrix();
      mesh.geometry.applyMatrix4(mesh.matrix).rotateY(Math.PI / 2);
      mesh.position.set(0, 0, 0);
      mesh.rotation.set(0, 0, 0);
    }
  }
  const batches = new Map<THREE.Material, THREE.Mesh[]>();
  for (const child of root.children) {
    const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material>;
    const batch = batches.get(mesh.material) ?? [];
    batch.push(mesh);
    batches.set(mesh.material, batch);
  }
  for (const batch of batches.values()) {
    if (batch.length < 2) continue;
    const nonIndexed = batch.some(mesh => !mesh.geometry.index);
    for (const mesh of batch) {
      if (nonIndexed && mesh.geometry.index) {
        const geometry = mesh.geometry;
        mesh.geometry = geometry.toNonIndexed();
        geometry.dispose();
      }
      mesh.updateMatrix();
      mesh.geometry.applyMatrix4(mesh.matrix);
    }
    const geometry = mergeGeometries(batch.map(mesh => mesh.geometry));
    if (!geometry) throw new Error(`Cannot merge weapon geometry: ${type}`);
    for (const mesh of batch) mesh.geometry.dispose();
    batch[0].geometry = geometry;
    batch[0].position.set(0, 0, 0);
    batch[0].rotation.set(0, 0, 0);
    for (const mesh of batch.slice(1)) root.remove(mesh);
  }
  return root;
}

/** Rebuild from rest vertices only when durability or folding changes, never cumulatively. */
export function setChairState(root: THREE.Group, uses: number, folded: boolean): void {
  if (root.name !== 'weapon-chair') return;
  const stage = Math.max(0, Math.min(3, 3 - uses));
  const key = `${stage}:${folded}`;
  if (root.userData.chairState === key) return;
  root.userData.chairState = key;
  for (const child of root.children) {
    const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>;
    const positions = mesh.geometry.getAttribute('position');
    const parts = mesh.geometry.getAttribute('chairPart');
    const rest = mesh.geometry.userData.chairRest ??= new Float32Array(positions.array);
    for (let i = 0; i < positions.count; i++) {
      let x = rest[i * 3], y = rest[i * 3 + 1], z = rest[i * 3 + 2];
      const part = parts.getX(i);
      if (folded && part === 1) { const oldY = y; y = 0.62 + z; z = -(oldY - 0.62); }
      if (folded && part === 2) z *= 0.12;
      const upper = Math.max(0, y - 0.65);
      x += stage * 0.2 * upper;
      z += stage * 0.16 * upper * upper;
      if (part === 1) z += stage * 0.13 * Math.max(0, 1 - Math.abs(x) / 0.34);
      if (y < 0.2 && x > 0) x += stage * 0.12 * (0.2 - y);
      // Rotate the broad face toward the broadcast camera, like the giant blade.
      positions.setXYZ(i, z, y - stage * 0.04 * upper, -x);
    }
    positions.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
    mesh.geometry.computeBoundingSphere();
    const base = mesh.material.userData.chairColor ??= mesh.material.color.clone();
    mesh.material.color.copy(base).multiplyScalar(1 - stage * 0.17);
  }
}
